import { Button, Checkbox, Icon, MuiMenuItem, TextField, Typography, strIsNullOrEmpty } from '@fox/atoms';
import { Flex, FlexRow } from '@fox/layout';
import MUITab from '@mui/material/Tab';
import MUITabs from '@mui/material/Tabs';
import MUITooltip from '@mui/material/Tooltip';
import styled from 'styled-components';
import { useContentContext } from '../../content-context';
import { useSwiftFoxTenantConfigContext } from '../../swiftfox-tenant-config-context';
import { useEventListContext } from './event-list-context';
import { translateDate } from './filters';
import { DateFilterOptions, LocationFilterOptions } from './util';

const TagContainer = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 1rem;
	flex-grow: 1;
	flex-wrap: wrap;
`;

const TagSection = styled.div`
	display: flex;
	justify-content: space-around;
	align-items: center;
	padding: 1rem;
	gap: 1rem;
	max-width: 970px;
	width: 100%;
`;

const TagBackgroundContainer = styled.div`
	background-color: ${props => props.theme.colors.grey[100]};
	width: 100%;
	display: flex;
	justify-content: center;
`;

const SearchTagContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 20px;
	font-size: 0.75rem;
	align-items: center;
	background-color: ${props => props.theme.colors.grey[300]};
	padding: 0.25rem 0.5rem;
	border-radius: 20px;
	max-width: 120px;
	cursor: pointer;
`;

const ButtonContainer = styled.div`
	justify-content: center;
	align-items: center;
	text-wrap: nowrap;
`;

function SearchTag({
	removeTag,
	tagValue,
	renderTag
}: {
	tagValue: string;
	removeTag: (tagValue: string) => void;
	renderTag?: (tagValue: string) => string;
}) {
	return (
		<MUITooltip title={tagValue}>
			<SearchTagContainer onClick={() => removeTag(tagValue)}>
				<Typography
					sx={{
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						overflow: 'hidden'
					}}
				>
					{renderTag?.(tagValue) ?? tagValue}
				</Typography>
				<Flex style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
					<Icon iconName="Cancel" />
				</Flex>
			</SearchTagContainer>
		</MUITooltip>
	);
}
export function CheckBoxSelect({
	value,
	label,
	onChange,
	options,
	allowMultiple,
	renderOption
}: {
	value: string | string[];
	label: string;
	onChange: (value: string | string[]) => void;
	options: string[];
	allowMultiple?: boolean;
	renderOption?: (option: string) => string;
}) {
	return (
		<TextField
			size="small"
			sx={{ width: '200px' }}
			label={label}
			value={value ?? ''}
			select
			SelectProps={{
				multiple: allowMultiple ?? false,
				renderValue: (value: string | string[]) => {
					let toRender;
					if (Array.isArray(value)) {
						toRender = value.map(v => renderOption?.(v) ?? v).join(', ');
					} else {
						toRender = renderOption?.(value) ?? value;
					}
					return (
						<Typography
							sx={{
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								lineHeight: 'inherit'
							}}
						>
							{toRender}
						</Typography>
					);
				}
			}}
			onChange={e => {
				onChange(e.target.value);
			}}
		>
			{options?.map(option => {
				const isChecked = Array.isArray(value) ? value?.includes(option) : value === option;
				const optionRender = renderOption?.(option) ?? option;
				return (
					<MuiMenuItem key={option} value={option}>
						<Flex style={{ justifyContent: 'space-between', alignItems: 'center' }}>
							<Checkbox checked={isChecked} />
							{optionRender}
						</Flex>
					</MuiMenuItem>
				);
			})}
		</TextField>
	);
}
export function DesktopFilters() {
	const {
		search,
		setSearch,
		dateFilter,
		displayMode,
		locationFilter,
		stateFilter,
		rsvpFilter,
		rsvpOptions,
		setDateFilter,
		setDisplayMode,
		setEndDate,
		setLocationFilter,
		setStateFilter,
		setRsvpFilter,
		setSelectedPage,
		setStartDate,
		events,
		total
	} = useEventListContext();
	const { isLoggedIn } = useContentContext();
	const { addressLocalisations } = useSwiftFoxTenantConfigContext();
	const stateFilterOptions = addressLocalisations.states;
	const stateDescription = addressLocalisations.addressParts.state;
	return (
		<div>
			<FlexRow style={{ padding: '1rem', justifyContent: 'center', gap: '1rem' }}>
				<TextField
					value={search ?? ''}
					onChange={e => setSearch(e.target.value)}
					sx={{ width: '200px' }}
					size="small"
					placeholder="Search"
					endAdornment={<Icon iconName="Search" />}
					inputProps={{
						style: { paddingLeft: 10, paddingRight: 15 }
					}}
				/>
				<CheckBoxSelect
					label="Location"
					allowMultiple
					onChange={(value: string[]) => {
						setLocationFilter(value);
						setSelectedPage(0);
					}}
					value={locationFilter}
					options={Object.values(LocationFilterOptions)}
				/>
				<CheckBoxSelect
					label={stateDescription}
					allowMultiple
					onChange={(value: string[]) => {
						setStateFilter(value);
						setSelectedPage(0);
					}}
					value={stateFilter}
					options={Object.values(stateFilterOptions)}
				/>
				{isLoggedIn && rsvpOptions?.length ? (
					<CheckBoxSelect
						label="RSVP response"
						allowMultiple
						onChange={(value: string[]) => {
							setRsvpFilter(value);
							setSelectedPage(0);
						}}
						value={rsvpFilter}
						options={rsvpOptions?.map(rsvp => rsvp.id)}
						renderOption={option => rsvpOptions?.find(o => o.id === option)?.displayName}
					/>
				) : null}
				<CheckBoxSelect
					label="Event date"
					onChange={(value: string) => {
						const { startDate, endDate } = translateDate(value);
						setStartDate(startDate);
						setEndDate(endDate);
						setDateFilter(value);
						setSelectedPage(0);
					}}
					value={dateFilter}
					options={Object.values(DateFilterOptions)}
				/>
			</FlexRow>
			<TagBackgroundContainer>
				<TagSection>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						Results showing for:
					</Typography>
					<TagContainer>
						{!strIsNullOrEmpty(search) ? (
							<SearchTag
								tagValue={search}
								removeTag={() => {
									setSearch(undefined);
									setSelectedPage(0);
								}}
							/>
						) : null}
						{locationFilter?.map(location => {
							return (
								<SearchTag
									key={location}
									tagValue={location}
									removeTag={tagValue => {
										setLocationFilter(prev => prev.filter(l => l !== tagValue));
										setSelectedPage(0);
									}}
								/>
							);
						})}
						{rsvpFilter?.map(rsvpOption => {
							return (
								<SearchTag
									key={rsvpOption}
									tagValue={rsvpOption}
									renderTag={tagValue => rsvpOptions?.find(o => o.id === tagValue)?.displayName}
									removeTag={tagValue => {
										setRsvpFilter(prev => prev.filter(l => l !== tagValue));
										setSelectedPage(0);
									}}
								/>
							);
						})}
						{stateFilter?.map(state => {
							return (
								<SearchTag
									key={state}
									tagValue={state}
									removeTag={tagValue => {
										setStateFilter(prev => prev.filter(l => l !== tagValue));
										setSelectedPage(0);
									}}
								/>
							);
						})}
						{!strIsNullOrEmpty(dateFilter) ? (
							<SearchTag
								tagValue={dateFilter}
								removeTag={() => {
									setDateFilter('All events');
									setStartDate(undefined);
									setEndDate(undefined);
									setSelectedPage(0);
								}}
							/>
						) : null}
					</TagContainer>
					<ButtonContainer>
						<Button
							variant="primary"
							onClick={() => {
								setSearch(undefined);
								setStartDate('@today');
								setEndDate(undefined);
								setLocationFilter([]);
								setStateFilter([]);
								setDateFilter('All upcoming');
								setRsvpFilter([]);
								setSelectedPage(0);
							}}
						>
							Clear filters
						</Button>
					</ButtonContainer>
				</TagSection>
			</TagBackgroundContainer>

			<Flex
				style={{
					justifyContent: 'flex-end',
					alignItems: 'flex-end',
					paddingRight: '2rem',
					gap: '1rem'
				}}
			>
				<Typography sx={{ opacity: 0.6 }}>
					{events !== undefined ? `(Showing ${events?.length} of ${total} events)` : null}
				</Typography>
				<MUITabs variant="standard" value={displayMode} onChange={(_, value) => setDisplayMode(value)}>
					<MUITab
						value="list"
						label={
							<MUITooltip title="List view">
								<div>
									<Icon sx={{ fontSize: '2rem' }} iconName="DragHandle" />
								</div>
							</MUITooltip>
						}
					/>
					<MUITab
						value="compact"
						label={
							<MUITooltip title="Grid view">
								<div>
									<Icon sx={{ fontSize: '2rem' }} iconName="GridView" />
								</div>
							</MUITooltip>
						}
					/>
				</MUITabs>
			</Flex>
		</div>
	);
}
